'use client';

import { motion } from 'motion/react';
import Image from 'next/image';

import { REVEAL_STAGGER, TextReveal } from '@/components/elements/text-reveal';
import { cn } from '@/lib/utils';

interface Feature {
  title: string;
  description: string;
  /**
   * Optional themed line-icon — base filename present in both
   * `/public/icons_redesign/black_icon` and `/white_icon` (e.g. "camera_setting").
   * When set, the icon replaces the decorative photo card and adapts to the
   * theme (black art on light, white art on dark).
   */
  icon?: string;
}

interface FeatureGridProps {
  title: string;
  description?: string;
  feature1: Feature;
  feature2: Feature;
  feature3: Feature;
  feature4: Feature;
  /** Per-use-case photo cards (label + src). Rotation stays fixed; falls back to defaults per slot. */
  cards?: { label: string; src: string }[];
}

// Decorative photo cards paired with the four features (default content + layout).
const PHOTO_CARDS = [
  { label: '💍 The big day', src: '/images/hero/cards/big-day.jpg', rotate: -2 },
  {
    label: '🎉 Festival night',
    src: '/images/hero/cards/festival-night.jpg',
    rotate: 2,
  },
  {
    label: '🌙 After dark',
    src: '/images/hero/cards/after-dark.jpg',
    rotate: -2.5,
  },
  {
    label: '🏖️ Beach day',
    src: '/images/hero/cards/beach-day.jpg',
    rotate: 2.5,
  },
];

export function FeatureGrid({
  title,
  description,
  feature1,
  feature2,
  feature3,
  feature4,
  cards,
}: FeatureGridProps) {
  const features = [feature1, feature2, feature3, feature4];

  return (
    <section className="section-padding">
      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, visible: {} }}
          className="mb-14 flex flex-col items-center gap-4 text-center md:mb-20"
        >
          <TextReveal mode="animate">
            <h2 className="font-display text-3xl tracking-tight text-balance md:text-[40px] md:leading-tight">
              {title}
            </h2>
          </TextReveal>
          {description && (
            <TextReveal mode="animate" delay={REVEAL_STAGGER * 2}>
              <p className="text-muted-foreground max-w-2xl text-base leading-relaxed">
                {description}
              </p>
            </TextReveal>
          )}
        </motion.div>

        <motion.div
          className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.1 } },
          }}
        >
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              className="flex flex-col items-center text-center"
              variants={{
                hidden: { opacity: 0, y: 24, filter: 'blur(4px)' },
                visible: {
                  opacity: 1,
                  y: 0,
                  filter: 'blur(0px)',
                  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                },
              }}
            >
              {feature.icon ? (
                <div
                  aria-hidden
                  className="mb-9 flex h-[160px] w-[172px] items-center justify-center"
                >
                  <Image
                    src={`/icons_redesign/black_icon/${feature.icon}.svg`}
                    alt=""
                    width={160}
                    height={160}
                    unoptimized
                    className="h-auto w-40 dark:hidden"
                  />
                  <Image
                    src={`/icons_redesign/white_icon/${feature.icon}.svg`}
                    alt=""
                    width={160}
                    height={160}
                    unoptimized
                    className="hidden h-auto w-40 dark:block"
                  />
                </div>
              ) : (
                <div
                  aria-hidden
                  className={cn('relative mb-9 w-[172px]')}
                  style={{ rotate: `${PHOTO_CARDS[i].rotate}deg` }}
                >
                  <div className="drop-shadow-[0px_6px_7px_rgba(0,0,0,0.12)]">
                    <Image
                      src={cards?.[i]?.src ?? PHOTO_CARDS[i].src}
                      alt=""
                      width={390}
                      height={296}
                      className="aspect-[195/148] w-full rounded-[21px] object-cover"
                    />
                    <span className="bg-secondary text-secondary-foreground absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full px-3.5 py-1 text-xs whitespace-nowrap">
                      {cards?.[i]?.label ?? PHOTO_CARDS[i].label}
                    </span>
                  </div>
                </div>
              )}
              <h3 className="font-display max-w-[252px] text-xl leading-[26px] tracking-tight">
                {feature.title}
              </h3>
              <p className="text-muted-foreground mt-2.5 max-w-[252px] text-sm leading-[22px]">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
